import * as React from 'react';
import {
  Table,
  TableBody,
  TableCell,
  TableContainer,
  TableHead,
  TableRow,
  Switch,
  IconButton,
  Menu,
  MenuItem,
  Paper,
  LinearProgress,
} from '@mui/material';
import MoreVertIcon from '@mui/icons-material/MoreVert';
import EditIcon from '@mui/icons-material/Edit';
import DeleteIcon from '@mui/icons-material/Delete';
import { TPhoneCaseBEItem } from 'api/phoneCases/phoneCases.types';
import { TPhoneCasesTable } from './PhoneCasesTable.types';
import { useAppDispatch, useAppSelector } from 'store/store';
import {
  deletePhoneCaseAsyncThunk,
  getPhoneCasesListAsyncThunk,
  togglePhoneCaseActiveAsyncThunk,
} from 'store/phoneCases/phoneCases.action';
import { resetPhoneCasesList } from 'store/phoneCases/phoneCases.slices';
import { Loader } from 'components';
import { useInitialLoaderCondition } from 'hooks/useInitialLoaderCondition';
import { useModals } from 'hooks/useModals';
import { MODALS } from 'store/contexts/ModalContext/modals';
import { useLocalize } from 'store/contexts/LocalizationContext/LocalizationContext.provider';

export const PhoneCasesTable: TPhoneCasesTable = () => {
  const modals = useModals();
  const localize = useLocalize();
  const dispatch = useAppDispatch();
  const {
    phoneCasesList: { data: phoneCasesList, isLoading: isPhoneCasesLoading },
    createPhoneCase: { isLoading: isCreatePhoneCaseLoading },
    deletePhoneCase: { isLoading: isDeletePhoneCaseLoading },
    updatePhoneCase: { isLoading: isUpdatePhoneCaseLoading },
    togglePhoneCaseActive: { isLoading: isTogglePhoneCaseActiveLoading },
  } = useAppSelector((store) => store.phoneCases);

  const {
    tagsList: { data: tagsList },
  } = useAppSelector((store) => store.tags);

  const isInitialLoadingFinished =
    useInitialLoaderCondition(isPhoneCasesLoading);

  const isLoading =
    isPhoneCasesLoading ||
    isCreatePhoneCaseLoading ||
    isDeletePhoneCaseLoading ||
    isUpdatePhoneCaseLoading ||
    isTogglePhoneCaseActiveLoading;

  const [anchorEl, setAnchorEl] = React.useState<{
    [key: string]: HTMLElement | null;
  }>({});

  const loadTableItems = React.useCallback(() => {
    dispatch(
      getPhoneCasesListAsyncThunk({ queryParams: { page: 1, perPage: 1000 } })
    );
  }, [dispatch]);

  const handleMenuOpen = (
    event: React.MouseEvent<HTMLElement>,
    itemId: string
  ) => {
    setAnchorEl({ [itemId]: event.currentTarget });
  };

  const handleMenuClose = (itemId: string) => {
    setAnchorEl((prev) => ({ ...prev, [itemId]: null }));
  };

  const handleEdit = (item: TPhoneCaseBEItem) => {
    handleMenuClose(item.id);
    modals.openModal(MODALS.ADMIN_PHONE_CASE_EDIT, { item });
  };

  const handleDelete = (item: TPhoneCaseBEItem) => {
    handleMenuClose(item.id);
    dispatch(
      deletePhoneCaseAsyncThunk({ id: item.id, onSuccess: loadTableItems })
    );
  };

  const handleToggleActive = (item: TPhoneCaseBEItem) => {
    dispatch(
      togglePhoneCaseActiveAsyncThunk({
        id: item.id,
        onSuccess: loadTableItems,
      })
    );
  };

  React.useEffect(() => {
    loadTableItems();
    return () => {
      dispatch(resetPhoneCasesList());
    };
  }, []);

  if (!isInitialLoadingFinished) return <Loader />;

  return (
    <div className="entity-table">
      <TableContainer component={Paper}>
        <Table>
          <TableHead>
            <TableRow sx={{ position: 'relative' }}>
              <TableCell className="entity-table__cell entity-table__cell--switch">
                Active
              </TableCell>
              <TableCell className="entity-table__cell">Name</TableCell>
              <TableCell className="entity-table__cell">Price</TableCell>
              <TableCell className="entity-table__cell">Tags</TableCell>
              <TableCell className="entity-table__cell">Actions</TableCell>
              {isLoading && (
                <LinearProgress
                  sx={{
                    position: 'absolute',
                    bottom: 0,
                    left: 0,
                    width: '100%',
                  }}
                />
              )}
            </TableRow>
          </TableHead>

          <TableBody>
            {phoneCasesList?.items?.map((item) => (
              <TableRow key={item.id} className="entity-table__row">
                <TableCell className="entity-table__cell entity-table__cell--switch">
                  <Switch
                    checked={item.isActive}
                    onChange={() => handleToggleActive(item)}
                    size="small"
                  />
                </TableCell>
                <TableCell className="entity-table__cell">
                  {localize(item.name) as string}
                </TableCell>
                <TableCell className="entity-table__cell">
                  $
                  {item.newPrice
                    ? `${item.newPrice.toFixed(2)}`
                    : `${item.price.toFixed(2)}`}
                  {item.newPrice && (
                    <span className="entity-table__old-price">
                      ${item.price.toFixed(2)}
                    </span>
                  )}
                </TableCell>
                <TableCell className="entity-table__cell">
                  <div className="entity-table__tags">
                    {item.tags.slice(0, 3).map((tag) => {
                      const tagData = tagsList?.items?.find(
                        (t) => t.id === tag
                      );

                      if (!tagData) return null;
                      return (
                        <span
                          key={tagData.id}
                          className="entity-table__tag"
                          style={{ backgroundColor: tagData?.color }}
                        >
                          {localize(tagData.name) as string}
                        </span>
                      );
                    })}
                    {item.tags.length > 3 && (
                      <span className="entity-table__tag-more">
                        +{item.tags.length - 3}
                      </span>
                    )}
                  </div>
                </TableCell>
                <TableCell className="entity-table__cell entity-table__cell--actions">
                  <IconButton
                    size="small"
                    onClick={(e) => handleMenuOpen(e, item.id)}
                    className="entity-table__menu-button"
                  >
                    <MoreVertIcon />
                  </IconButton>
                  <Menu
                    anchorEl={anchorEl[item.id]}
                    open={Boolean(anchorEl[item.id])}
                    onClose={() => handleMenuClose(item.id)}
                  >
                    <MenuItem
                      onClick={() => handleEdit(item)}
                      className="entity-table__menu-item"
                    >
                      <EditIcon fontSize="small" sx={{ mr: 1 }} />
                      Edit
                    </MenuItem>
                    <MenuItem
                      onClick={() => handleDelete(item)}
                      className="entity-table__menu-item entity-table__menu-item--delete"
                    >
                      <DeleteIcon fontSize="small" sx={{ mr: 1 }} />
                      Delete
                    </MenuItem>
                  </Menu>
                </TableCell>
              </TableRow>
            ))}
          </TableBody>
        </Table>
      </TableContainer>
    </div>
  );
};
